import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import AuthService, {
	AuthError,
	isRequires2FA,
	isRequires2FASetup,
	SSO_ERROR_STORAGE_KEY,
} from "../service/AuthService";
import OidcService from "../service/OidcService";
import MessageToast from "sap/m/MessageToast";
import Dialog from "sap/m/Dialog";
import Input from "sap/m/Input";
import Button from "sap/m/Button";
import Label from "sap/m/Label";
import VBox from "sap/m/VBox";
import MessageBox from "sap/m/MessageBox";
import Bar from "sap/m/Bar";
import Title from "sap/m/Title";
import Text from "sap/m/Text";
import Image from "sap/m/Image";

/**
 * @namespace com.ang.portal.controller
 */
export default class Login extends BaseController {
	private _o2FADialog: Dialog | null = null;
	private _oForgotPasswordDialog: Dialog | null = null;

	public onInit(): void {
		const oModel = new JSONModel({
			email: "",
			password: "",
			remember: false,
			forgotPasswordEmail: "",
			twoFactorCode: "",
			tempToken: "",
			busy: false,
			forgotPasswordBusy: false,
			twoFactorBusy: false,
			// Shows the email/password form while password sign-in is off; only the
			// startup admin is let through by the backend.
			adminSignIn: false,
			errorMessage: "",
			emailState: "None",
			emailStateText: "",
			pwdState: "None",
			// The application whose sign-in waits for this one, if any.
			continueTo: "",
		});
		this.setModel(oModel, "login");

		this.getRouter()
			.getRoute("login")
			.attachPatternMatched(() => this.onRouteMatched(), this);
	}

	private onRouteMatched(): void {
		if (AuthService.isAuthenticated()) {
			this.navToAfterSignIn();
			return;
		}
		// Reset state when returning to login
		this.resetState();
		void this.showContinueTo();
		void this.showSsoErrorIfPresent();
	}

	private async showContinueTo(): Promise<void> {
		const pending = OidcService.getPending();
		const bundle = await this.getResourceBundle();
		(this.getModel("login") as JSONModel).setProperty(
			"/continueTo",
			pending
				? bundle.getText("loginContinueTo", [pending.appName || bundle.getText("loginContinueToUnnamed")])
				: "",
		);
	}

	private async showSsoErrorIfPresent(): Promise<void> {
		const queryParams = new URLSearchParams(window.location.search);
		const queryError = queryParams.get("ssoError");
		const storedError = sessionStorage.getItem(SSO_ERROR_STORAGE_KEY);
		const ssoError = queryError || storedError;

		if (!ssoError) {
			return;
		}

		if (queryError) {
			queryParams.delete("ssoError");
			const newSearch = queryParams.toString();
			const newUrl =
				window.location.pathname +
				(newSearch ? `?${newSearch}` : "") +
				window.location.hash;
			window.history.replaceState(null, "", newUrl);
		}
		if (storedError) {
			sessionStorage.removeItem(SSO_ERROR_STORAGE_KEY);
		}

		const bundle = await this.getResourceBundle();
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty(
			"/errorMessage",
			bundle.getText(`ssoError_${ssoError}`, [], true) ||
				bundle.getText("ssoErrorGeneric"),
		);
	}

	public onMicrosoftLoginPress(): void {
		AuthService.loginWithMicrosoft();
	}

	public onAdminSignInToggle(): void {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/adminSignIn", !oModel.getProperty("/adminSignIn"));
		oModel.setProperty("/errorMessage", "");
	}

	private resetState(): void {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/tempToken", "");
		oModel.setProperty("/twoFactorCode", "");
		oModel.setProperty("/forgotPasswordEmail", "");
		oModel.setProperty("/adminSignIn", false);
		oModel.setProperty("/errorMessage", "");
	}

	public async onForgotPasswordPress(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty(
			"/forgotPasswordEmail",
			(oModel.getProperty("/email") as string) || "",
		);
		await this.openForgotPasswordDialog();
	}

	private async openForgotPasswordDialog(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/errorMessage", "");

		if (!this._oForgotPasswordDialog) {
			const bundle = await this.getResourceBundle();

			const oEmailInput = new Input({
				id: this.createId("forgotPasswordEmailInput"),
				type: "Email",
				value: "{login>/forgotPasswordEmail}",
				placeholder: bundle.getText("forgotPasswordEmailPlaceholder"),
				enabled: "{= !${login>/forgotPasswordBusy}}",
				width: "100%",
				submit: () => void this.onForgotPasswordSubmit(),
			});

			const oContent = new VBox({
				items: [
					new Label({
						text: bundle.getText("forgotPasswordEmailLabel"),
						required: true,
					}),
					oEmailInput,
				],
			}).addStyleClass("sapUiSmallMargin");

			const oCustomHeader = new Bar({
				contentMiddle: [
					new Title({ text: bundle.getText("forgotPasswordDialogTitle") }),
				],
				contentRight: [
					new Button({
						icon: "sap-icon://decline",
						type: "Transparent",
						press: () => this.closeForgotPasswordDialog(),
					}),
				],
			});

			this._oForgotPasswordDialog = new Dialog({
				id: this.createId("forgotPasswordDialog"),
				customHeader: oCustomHeader,
				type: "Message",
				contentWidth: "24rem",
				content: [oContent],
				beginButton: new Button({
					text: bundle.getText("forgotPasswordSubmitButton"),
					type: "Emphasized",
					enabled: "{= !${login>/forgotPasswordBusy}}",
					press: () => void this.onForgotPasswordSubmit(),
				}),
				afterOpen: () => {
					setTimeout(() => oEmailInput.focus(), 100);
				},
				escapeHandler: () => this.closeForgotPasswordDialog(),
			});

			this._oForgotPasswordDialog.setModel(oModel, "login");
			this.getView()?.addDependent(this._oForgotPasswordDialog);
		}

		this._oForgotPasswordDialog.open();
	}

	private closeForgotPasswordDialog(): void {
		if (this._oForgotPasswordDialog) {
			this._oForgotPasswordDialog.close();
		}
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/forgotPasswordBusy", false);
	}

	private async onForgotPasswordSubmit(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		const email = (oModel.getProperty("/forgotPasswordEmail") as string).trim();
		const bundle = await this.getResourceBundle();

		if (!email) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("forgotPasswordErrorRequired"),
			);
			return;
		}

		oModel.setProperty("/forgotPasswordBusy", true);
		oModel.setProperty("/errorMessage", "");

		try {
			const result = await AuthService.forgotPassword(email);
			this.closeForgotPasswordDialog();
			if (result.debugResetUrl) {
				console.info("[ForgotPassword] Debug reset URL:", result.debugResetUrl);
			}

			MessageBox.information(bundle.getText("forgotPasswordSuccess"), {
				title: bundle.getText("forgotPasswordDialogTitle"),
			});
		} catch (error) {
			oModel.setProperty(
				"/errorMessage",
				error instanceof Error && error.message
					? error.message
					: bundle.getText("forgotPasswordErrorGeneric"),
			);
		} finally {
			oModel.setProperty("/forgotPasswordBusy", false);
		}
	}

	/**
	 * Read a field from its DOM node, falling back to the model.
	 *
	 * Browser autofill writes straight to the input element without firing the
	 * change event sap.m.Input listens for, so the model can hold an older value
	 * than the one the user is looking at - that is how a login form showing the
	 * right address posted a stale one. The DOM is the source of truth; the model
	 * is resynced so validation and valueState stay consistent with it.
	 */
	private readFieldValue(sId: string, sPath: string): string {
		const oModel = this.getModel("login") as JSONModel;
		const oInput = this.byId(sId) as Input | undefined;
		const oDomRef = oInput?.getDomRef("inner") as HTMLInputElement | null;
		const sValue = oDomRef
			? oDomRef.value
			: ((oModel.getProperty(sPath) as string) ?? "");

		oModel.setProperty(sPath, sValue);
		return sValue;
	}

	public async onLoginPress(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		// Trimmed: a pasted address with a stray space fails as "Invalid login",
		// which is indistinguishable from a wrong password. The password is left
		// untouched - whitespace there may be deliberate.
		const email = this.readFieldValue("emailInput", "/email").trim();
		oModel.setProperty("/email", email);
		const password = this.readFieldValue("passwordInput", "/password");
		const bundle = await this.getResourceBundle();

		// Validate with Fiori valueState styling
		let bOk = true;
		if (!email) {
			oModel.setProperty("/emailState", "Error");
			oModel.setProperty(
				"/emailStateText",
				bundle.getText("loginErrorRequired"),
			);
			bOk = false;
		} else {
			oModel.setProperty("/emailState", "None");
			oModel.setProperty("/emailStateText", "");
		}
		if (!password) {
			oModel.setProperty("/pwdState", "Error");
			bOk = false;
		} else {
			oModel.setProperty("/pwdState", "None");
		}
		if (!bOk) {
			return;
		}

		oModel.setProperty("/busy", true);
		oModel.setProperty("/errorMessage", "");

		try {
			const result = await AuthService.login(email, password);

			if (isRequires2FA(result)) {
				// 2FA verification required - open verification dialog
				oModel.setProperty("/tempToken", result.tempToken);
				oModel.setProperty("/busy", false);
				void this.open2FADialog();
				return;
			}

			if (isRequires2FASetup(result)) {
				// Mandatory 2FA, none set up yet - enrol before the session opens
				oModel.setProperty("/busy", false);
				await this.open2FAEnrolDialog(result.setupToken);
				return;
			}

			// Normal login success
			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("loginWelcome", [result.user.name]));
			this.navToAfterSignIn();
		} catch (error) {
			const disabled =
				error instanceof AuthError && error.code === "password_login_disabled";
			oModel.setProperty(
				"/errorMessage",
				(await this.getResourceBundle()).getText(
					disabled ? "loginErrorPasswordDisabled" : "loginErrorInvalid",
				),
			);
		} finally {
			oModel.setProperty("/busy", false);
		}
	}

	private async open2FADialog(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/twoFactorCode", "");

		if (!this._o2FADialog) {
			const bundle = await this.getResourceBundle();

			const oCodeInput = new Input({
				id: this.createId("twoFactorInput"),
				type: "Text",
				maxLength: 10,
				value: "{login>/twoFactorCode}",
				placeholder: bundle.getText("login2FAPlaceholder"),
				enabled: "{= !${login>/twoFactorBusy}}",
				width: "100%",
				submit: () => void this.onVerify2FA(),
			});

			const oContent = new VBox({
				items: [
					new Label({ text: bundle.getText("login2FALabel"), required: true }),
					oCodeInput,
				],
			}).addStyleClass("sapUiSmallMargin");

			const oCustomHeader = new Bar({
				contentMiddle: [
					new Title({ text: bundle.getText("login2FADialogTitle") }),
				],
				contentRight: [
					new Button({
						icon: "sap-icon://decline",
						type: "Transparent",
						press: () => this.close2FADialog(),
					}),
				],
			});

			this._o2FADialog = new Dialog({
				id: this.createId("twoFactorDialog"),
				customHeader: oCustomHeader,
				type: "Message",
				contentWidth: "20rem",
				content: [oContent],
				beginButton: new Button({
					text: bundle.getText("login2FAButtonText"),
					type: "Emphasized",
					enabled: "{= !${login>/twoFactorBusy}}",
					press: () => void this.onVerify2FA(),
				}),
				afterOpen: () => {
					setTimeout(() => oCodeInput.focus(), 100);
				},
				escapeHandler: () => this.close2FADialog(),
			});

			this._o2FADialog.setModel(oModel, "login");
			this.getView()?.addDependent(this._o2FADialog);
		}

		this._o2FADialog.open();
	}

	/**
	 * Mandatory 2FA, first sign-in without it: set up an authenticator before the
	 * session exists. A wrong code keeps the dialog open; the setup token stays
	 * valid for its lifetime, so the user can retry.
	 */
	private async open2FAEnrolDialog(setupToken: string): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		const bundle = await this.getResourceBundle();

		let setup: { qrCode: string; secret: string };
		try {
			setup = await AuthService.startEnrol2FA(setupToken);
		} catch (error) {
			oModel.setProperty(
				"/errorMessage",
				error instanceof Error && error.message
					? error.message
					: bundle.getText("login2FAEnrolError"),
			);
			return;
		}

		oModel.setProperty("/twoFactorCode", "");

		const onConfirm = async (): Promise<void> => {
			const code = (
				(oModel.getProperty("/twoFactorCode") as string) || ""
			).trim();
			if (code.length < 6) {
				MessageBox.error(bundle.getText("profile2FACodeInvalid"));
				return;
			}

			oModel.setProperty("/twoFactorBusy", true);
			try {
				const result = await AuthService.completeEnrol2FA(setupToken, code);
				oDialog.close();
				MessageBox.information(
					bundle.getText("login2FAEnrolBackupCodes") +
						"\n\n" +
						result.backupCodes.join("\n"),
					{
						title: bundle.getText("profile2FABackupCodesTitle"),
						onClose: () => {
							MessageToast.show(
								bundle.getText("loginWelcome", [result.user.name]),
							);
							this.navToAfterSignIn();
						},
					},
				);
			} catch (error) {
				MessageBox.error(
					error instanceof Error && error.message
						? error.message
						: bundle.getText("login2FAErrorInvalid"),
				);
			} finally {
				oModel.setProperty("/twoFactorBusy", false);
			}
		};

		const oCodeInput = new Input({
			type: "Text",
			maxLength: 10,
			value: "{login>/twoFactorCode}",
			placeholder: bundle.getText("login2FAPlaceholder"),
			enabled: "{= !${login>/twoFactorBusy}}",
			width: "100%",
			submit: () => void onConfirm(),
		});

		const oDialog = new Dialog({
			title: bundle.getText("login2FAEnrolTitle"),
			contentWidth: "24rem",
			content: [
				new VBox({
					alignItems: "Center",
					items: [
						new Text({
							text: bundle.getText("login2FAEnrolIntro"),
							wrapping: true,
						}).addStyleClass("sapUiSmallMarginBottom"),
						new Image({
							src: setup.qrCode,
							width: "200px",
							height: "200px",
						}).addStyleClass("sapUiSmallMarginBottom"),
						new Label({ text: bundle.getText("profile2FASetupSecretLabel") }),
						new Text({ text: setup.secret, wrapping: true }).addStyleClass(
							"sapUiTinyMarginBottom",
						),
						new Label({
							text: bundle.getText("profile2FASetupCodeLabel"),
							required: true,
						}).addStyleClass("sapUiSmallMarginTop"),
						oCodeInput,
					],
				}).addStyleClass("sapUiSmallMargin"),
			],
			beginButton: new Button({
				text: bundle.getText("login2FAButtonText"),
				type: "Emphasized",
				enabled: "{= !${login>/twoFactorBusy}}",
				press: () => void onConfirm(),
			}),
			endButton: new Button({
				text: bundle.getText("login2FACancelButton"),
				press: () => oDialog.close(),
			}),
			afterOpen: () => {
				setTimeout(() => oCodeInput.focus(), 100);
			},
			afterClose: () => {
				oModel.setProperty("/twoFactorCode", "");
				oDialog.destroy();
			},
		});

		oDialog.setModel(oModel, "login");
		this.getView()?.addDependent(oDialog);
		oDialog.open();
	}

	private close2FADialog(): void {
		if (this._o2FADialog) {
			this._o2FADialog.close();
		}
		const oModel = this.getModel("login") as JSONModel;
		oModel.setProperty("/twoFactorCode", "");
		oModel.setProperty("/tempToken", "");
	}

	private async onVerify2FA(): Promise<void> {
		const oModel = this.getModel("login") as JSONModel;
		const tempToken = oModel.getProperty("/tempToken") as string;
		const code = oModel.getProperty("/twoFactorCode") as string;
		const bundle = await this.getResourceBundle();

		if (!code || code.length < 6) {
			this.close2FADialog();
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("login2FAErrorRequired"),
			);
			return;
		}

		oModel.setProperty("/twoFactorBusy", true);

		try {
			const result = await AuthService.verify2FA(tempToken, code);
			this.close2FADialog();
			MessageToast.show(bundle.getText("loginWelcome", [result.user.name]));
			this.navToAfterSignIn();
		} catch {
			this.close2FADialog();
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("login2FAErrorInvalid"),
			);
		} finally {
			oModel.setProperty("/twoFactorBusy", false);
		}
	}

	public onContactAdmin(): void {
		// Open mailto link or support route
		window.location.href =
			"mailto:support@ang.ch?subject=Portal Access Request";
	}
}
